import { APIv4 as API } from 'src/libs/axios';
import { alertError } from 'src/libs/toastify';
import { NomenclatureType, VatType } from '../constants';
import { fetchAllNomenclatureCategories } from '../DirectoriesPage/NomenclatureCategoriesPage/services';
import {
  NomenclatureCategory,
  NomenclatureEnumItem,
  NomenclatureListItem,
  NomenclatureTag,
} from '../types';

const Messages = {
  FETCH_NOMENCLATURES: 'Ошибка получения списка номенклатур',
  FETCH_NOMENCLATURE: 'Ошибка получения данных о номенклатуре',
  CREATE_NOMENCLATURE: 'Ошибка создания номенклатуры',
  UPDATE_NOMENCLATURE: 'Ошибка обновления номенклатуры',
  DELETE_NOMENCLATURE: 'Ошибка удаления номенклатуры',
  FETCH_ENUMS: 'Ошибка получения справочных данных',
  CALC_COST: 'Ошибка расчета себестоимости',
  CALC_NUTRITION: 'Ошибка расчета БЖУ',
};

// GET nomenclature with {id}
export type NomenclatureDetail = {
  id: string;
  name: string;
  fullName: string;
  shortName: string;
  type: number;
  externalCode: string;
  unitOfMeasurement: number;
  categoryId: string | null;
  baseUnitCost: number;
  weightPerUnit: number;
  proteins: number;
  fats: number;
  carbohydrates: number;
  calories: number;
  vat: number | null;
  packagingInHallId: string | null;
  packagingTakeawayId: string | null;
  tagIds: string[];
};

// POST/PUT
export type NomenclaturePayload = {
  name: string;
  fullName: string;
  shortName: string;
  type: number;
  externalCode: string;
  unitOfMeasurement: number;
  categoryId: string | null;
  baseUnitCost: number;
  weightPerUnit: number;
  proteins: number;
  fats: number;
  carbohydrates: number;
  calories: number;
  vat: number;
  packagingInHallId: string | null;
  packagingTakeawayId: string | null;
  tagIds: string[];
};

// form value
export type NomenclatureValues = {
  name: string;
  fullName: string;
  shortName: string;
  type: number | null;
  externalCode: string;
  unitOfMeasurement: number | null;
  category: NomenclatureCategory | null;
  baseUnitCost: number;
  weightPerUnit: number;
  proteins: number | null;
  fats: number | null;
  carbohydrates: number | null;
  calories: number | null;
  vat: number | null;
  packagingInHall: NomenclatureListItem | null;
  packagingTakeaway: NomenclatureListItem | null;
  tags: NomenclatureTag[];
};

export type CostCalculation = {
  costPerBaseUnit: number;
  isCalculated: boolean;
};

export type NutritionCalculation = {
  proteins: number;
  fats: number;
  carbohydrates: number;
  calories: number;
};

export type NomenclatureSearchParams = {
  searchName?: string;
  categoryId?: string;
  type?: number;
};

const BASE_URL = '/back-office/nomenclatures';
const ENUMS_URL = '/back-office/enums';

// form -> API payload
const adaptNomenclature = (
  values: NomenclatureValues
): NomenclaturePayload => ({
  name: values.name,
  fullName: values.fullName,
  shortName: values.shortName,
  type: values.type ?? 0,
  externalCode: values.externalCode,
  unitOfMeasurement: values.unitOfMeasurement ?? 0,
  categoryId: values.category?.id ?? null,
  baseUnitCost: values.baseUnitCost,
  weightPerUnit: values.weightPerUnit,
  proteins: values.proteins ?? 0,
  fats: values.fats ?? 0,
  carbohydrates: values.carbohydrates ?? 0,
  calories: values.calories ?? 0,
  vat: values.vat ?? VatType.WITHOUT,
  packagingInHallId: values.packagingInHall?.id ?? null,
  packagingTakeawayId: values.packagingTakeaway?.id ?? null,
  tagIds: values.tags.map((tag) => tag.id),
});

// nomenclature detail -> form values
const mapNomenclatureToValues = (
  data: NomenclatureDetail,
  packagingList: NomenclatureListItem[] = [],
  categoriesList: NomenclatureCategory[] = []
): NomenclatureValues => ({
  name: data.name ?? '',
  fullName: data.fullName ?? '',
  shortName: data.shortName ?? '',
  type: data.type,
  externalCode: data.externalCode ?? '',
  unitOfMeasurement: data.unitOfMeasurement,
  category: data.categoryId
    ? categoriesList.find((c) => c.id === data.categoryId) ?? {
        id: data.categoryId,
        name: '',
      }
    : null,
  baseUnitCost: data.baseUnitCost ?? 0,
  weightPerUnit: data.weightPerUnit ?? 0,
  proteins: data.proteins ?? null,
  fats: data.fats ?? null,
  carbohydrates: data.carbohydrates ?? null,
  calories: data.calories ?? null,
  vat: data.vat ?? VatType.WITHOUT,
  packagingInHall: data.packagingInHallId
    ? packagingList.find((p) => p.id === data.packagingInHallId) ?? {
        id: data.packagingInHallId,
        name: '',
        fullName: '',
        shortName: '',
        type: NomenclatureType.PACKAGING,
        externalCode: '',
        unitOfMeasurement: 0,
        category: null,
        baseUnitCost: 0,
        weightPerUnit: 0,
        proteins: 0,
        fats: 0,
        carbohydrates: 0,
        calories: 0,
        tags: [],
      }
    : null,
  packagingTakeaway: data.packagingTakeawayId
    ? packagingList.find((p) => p.id === data.packagingTakeawayId) ?? {
        id: data.packagingTakeawayId,
        name: '',
        fullName: '',
        shortName: '',
        type: NomenclatureType.PACKAGING,
        externalCode: '',
        unitOfMeasurement: 0,
        category: null,
        baseUnitCost: 0,
        weightPerUnit: 0,
        proteins: 0,
        fats: 0,
        carbohydrates: 0,
        calories: 0,
        tags: [],
      }
    : null,
  tags: data.tagIds?.map((id) => ({ id, name: '' })) ?? [],
});

// CRUD
export const fetchAllNomenclatures = (
  params?: NomenclatureSearchParams
): Promise<NomenclatureListItem[] | void> =>
  API.get<NomenclatureListItem[]>(BASE_URL, { params })
    .then(({ data }) => data)
    .catch((err) => alertError(err, Messages.FETCH_NOMENCLATURES));

export const fetchNomenclature = async ({
  id,
}: {
  id: string;
}): Promise<NomenclatureValues | void> => {
  try {
    const { data } = await API.get<NomenclatureDetail>(`${BASE_URL}/${id}`);

    const categoriesList = await fetchAllNomenclatureCategories();

    let packagingList: NomenclatureListItem[] = [];
    if (
      data.type === NomenclatureType.DISH ||
      data.type === NomenclatureType.GOODS
    ) {
      packagingList =
        (await fetchAllNomenclatures({ type: NomenclatureType.PACKAGING })) ??
        [];
    }

    return mapNomenclatureToValues(data, packagingList, categoriesList ?? []);
  } catch (err) {
    alertError(err, Messages.FETCH_NOMENCLATURE);
  }
};

export const addNomenclature = (data: NomenclatureValues): Promise<string> =>
  API.post<string>(BASE_URL, adaptNomenclature(data)).then(({ data }) => data);

export const updateNomenclature = (params: {
  id: string;
  data: NomenclatureValues;
}): Promise<boolean> =>
  API.put(`${BASE_URL}/${params.id}`, adaptNomenclature(params.data)).then(
    ({ data }) => Boolean(data)
  );

export const deleteNomenclature = ({ id }: { id: string }): Promise<boolean> =>
  API.delete(`${BASE_URL}/${id}`).then(({ data }) => Boolean(data));

// Расчеты
export const fetchCostCalculation = (params: {
  id: string;
  shopId: number;
}): Promise<CostCalculation | void> =>
  API.get<CostCalculation>(`${BASE_URL}/${params.id}/calc/cost`, {
    params: { shopId: params.shopId },
  })
    .then(({ data }) => data)
    .catch((err) => alertError(err, Messages.CALC_COST));

export const fetchNutritionCalculation = (params: {
  id: string;
  shopId?: number;
}): Promise<NutritionCalculation | void> =>
  API.get<NutritionCalculation>(`${BASE_URL}/${params.id}/calc/nutrition`, {
    params: params.shopId ? { shopId: params.shopId } : undefined,
  })
    .then(({ data }) => data)
    .catch((err) => alertError(err, Messages.CALC_NUTRITION));

export const fetchNomenclatureTypes = (): Promise<
  NomenclatureEnumItem[] | void
> =>
  API.get<NomenclatureEnumItem[]>(`${ENUMS_URL}/nomenclature-types`)
    .then(({ data }) => data)
    .catch((err) => alertError(err, Messages.FETCH_ENUMS));

export const fetchProcessingTypeCategories = (): Promise<
  NomenclatureEnumItem[] | void
> =>
  API.get<NomenclatureEnumItem[]>(`${ENUMS_URL}/processing-type-categories`)
    .then(({ data }) => data)
    .catch((err) => alertError(err, Messages.FETCH_ENUMS));

export const fetchUnitOfMeasurements = (): Promise<
  NomenclatureEnumItem[] | void
> =>
  API.get<NomenclatureEnumItem[]>(`${ENUMS_URL}/unit-of-measurements`)
    .then(({ data }) => data)
    .catch((err) => alertError(err, Messages.FETCH_ENUMS));

export const fetchVats = (): Promise<NomenclatureEnumItem[] | void> =>
  API.get<NomenclatureEnumItem[]>(`${ENUMS_URL}/vats`)
    .then(({ data }) => data)
    .catch((err) => alertError(err, Messages.FETCH_ENUMS));
